import React, { useState, useEffect } from "react";
import { Skeleton, Divider } from "antd";
import { Contact } from "../../../types/contact";
import SearchInput from "../../../components/SearchInput";
import ContactItem from "../../../components/ContactItem";
import styles from "./ContactList.module.scss";
import { useVirtualScroll } from "../../../hooks/useVirtualScroll";
import { useSearchParamsV6 } from "../../../hooks/useSearchParamsV6";
import { useGetContacts } from "../../../hooks/queries/contact";

interface ContactListProps {}

const ITEM_HEIGHT = 72; // Chiều cao cố định cho mỗi ChatItem
const CONTAINER_HEIGHT = 600; // Chiều cao của container có thể cuộn

const ContactList: React.FC<ContactListProps> = () => {
  const [, setSearchParams] = useSearchParamsV6();

  // Search state with debounce
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetContacts({
    keyword: debouncedSearch,
  });

  const contacts = React.useMemo(() => {
    return data?.pages.flatMap((page) => page.Data) || [];
  }, [data]);

  const loading = isLoading && !isFetchingNextPage;

  // Hook cuộn ảo
  const {
    virtualItems,
    totalHeight,
    scrollElementRef,
    handleScroll: handleVirtualScroll,
  } = useVirtualScroll({
    itemCount: contacts.length,
    itemHeight: ITEM_HEIGHT,
    containerHeight: CONTAINER_HEIGHT,
    overscan: 5, // Render thêm 5 item bên trên/dưới khung nhìn
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    handleVirtualScroll(e);
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 300 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleContactClick = (contact: Contact) => {
    // Set URL params: ?id={contactId}&t=contact
    // This will trigger useGetInfoRoom to create/join the room
    const params = new URLSearchParams();
    params.set("id", contact.Id);
    params.set("t", "contact");
    setSearchParams(params);
  };

  // Item skeleton khi đang tải
  const SkeletonItem = () => (
    <div style={{ padding: "12px 16px" }}>
      <Skeleton avatar={{ size: 48 }} title={false} paragraph={{ rows: 2, width: ["60%", "40%"] }} active />
    </div>
  );

  if (loading) {
    return (
      <div className={styles.contactList}>
        <div className={styles.searchBox}>
          <SearchInput value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <div className={styles.contactListContent}>
          {[...Array(5)].map((_, index) => (
            <React.Fragment key={index}>
              <SkeletonItem />
              {index < 4 && <Divider style={{ margin: 0 }} />}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.contactList}>
        <div className={styles.searchBox}>
          <SearchInput value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <div className={styles.emptyState}>Lỗi khi tải dữ liệu</div>
      </div>
    );
  }

  return (
    <div className={styles.contactList}>
      <div className={styles.searchBox}>
        <SearchInput value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
      </div>
      <div
        ref={scrollElementRef}
        onScroll={handleScroll}
        className={styles.contactListContent}
        style={{
          height: CONTAINER_HEIGHT,
          overflow: "auto",
        }}
      >
        {contacts.length === 0 ? (
          <div className={styles.emptyState}>Không tìm thấy liên hệ</div>
        ) : (
          <div style={{ height: totalHeight, position: "relative" }}>
            {virtualItems.map((virtualItem) => {
              const contact = contacts[virtualItem.index];
              return (
                <div
                  key={contact.Id}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: virtualItem.size,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <ContactItem contact={contact} onClick={() => handleContactClick(contact)} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ContactList);
